import React, { useEffect } from 'react';
import { useMasterAccountId } from '../../../hooks/redux';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import ActionableHeaderCard from 'ps-components/Card/ActionableHeaderCard';
import Dropdown from 'ps-components/Dropdown/Dropdown';
import DropdownViews from 'ps-components/Dropdown/DropdownViews';
import SlideoutHeader from 'ps-components/Slideout/Header/SlideoutHeader';
import { CATEGORY_CODE, PAGE, PAGE_CODE } from '../../../lib/pageCodeDefaults';
import { SLIDEOUTS as MP_SLIDEOUTS } from '../../Slideout/Slideouts';
import {
  getJobDetailsSelectedView,
  getJobDetailsViews,
  getJobDetails,
  setJobDetailsSelectedView
} from './JobDetailsActions';
import './JobDetails.scss';

function JobDetails(props) {
  const {
    actions,
    groupAdminCanConfigureViews,
    hideSlideout,
    jobCode,
    jobCodeKey,
    jobDetails,
    regularUserCanConfigureViews,
    selectedViewPk,
    showSlideout,
    userRights,
    views,
    showJobCodeKey
  } = props;

  const masterAccountId = useMasterAccountId();

  useEffect(() => {
    actions.getJobDetailsViews(masterAccountId);
    actions.getJobDetailsSelectedView(masterAccountId);
  }, []);

  useEffect(() => {
    if (selectedViewPk) {
      const options = {
        jobCode,
        jobCodeKey,
        viewPk: selectedViewPk
      };

      actions.getJobDetails(masterAccountId, options);
    }
  }, [selectedViewPk, jobCode, jobCodeKey]);

  function getSelectedViewName(viewPk) {
    const selectedView = views?.find(v => v.view_pk === viewPk);

    return selectedView?.view_name;
  }

  function getViewDropdown() {
    return (
      <div className="job-details__dropdown-group">
        <label>Job Details View</label>
        <Dropdown
          className="job-details__dropdown"
          customRenderComponent={DropdownViews}
          customRenderProps={{
            groupAdminCanConfigureViews,
            onEdit: showConfigureViewSlideout,
            onSelectView: setSelectedPageView,
            regularUserCanConfigureViews,
            selectedViewPk,
            userRights,
            views
          }}
          text={getSelectedViewName(selectedViewPk)}
        />
      </div>
    );
  }

  function onClose() {
    hideSlideout();
  }

  function refreshViews() {
    actions.getJobDetailsViews(masterAccountId).then(() => {
      actions.getJobDetailsSelectedView(masterAccountId);
    });
  }

  function renderJobDetailData() {
    if (jobDetails?.data?.length) {
      const jobCodeKeyIndex = !showJobCodeKey
        ? jobDetails.terms.findIndex(term => term.fieldName == 'org_job_code_key')
        : -1;
      const data = jobDetails.data[0];
      const terms = jobDetails.terms.map((term, index) => ({ [term.displayName]: data[index] || '' }));
      const filteredTerms = jobCodeKeyIndex !== -1 ? terms.filter((_, index) => index !== jobCodeKeyIndex) : terms;

      const dataColumns = [];
      const rowCount = filteredTerms.length / 3;
      const remainder = filteredTerms.length % 3;
      let numberOfRows = 1;

      for (let i = 0; i < filteredTerms.length; i = 0) {
        const increment = numberOfRows <= remainder ? rowCount + 1 : rowCount;
        const dataRow = filteredTerms.splice(i, increment);
        dataColumns.push(dataRow);
        numberOfRows += 1;
      }

      return dataColumns.map(column => (
        <div className="job-details__column">
          {column.map(datum => {
            const values = Object.entries(datum)[0];
            return (
              <div className="job-details__cell">
                <span className="job-details__cell-key">{`${values[0]}: `}</span>
                <span className="job-details__cell-value">{`${values[1]}`}</span>
              </div>
            );
          })}
        </div>
      ));
    }

    return null;
  }

  function setSelectedPageView(viewName) {
    const selectedView = views?.find(view => view.view_name?.trim() === viewName?.trim());
    if (selectedView) {
      actions.setJobDetailsSelectedView(masterAccountId, selectedView.view_pk);
    }
  }

  function showConfigureViewSlideout() {
    showSlideout(MP_SLIDEOUTS.CONFIGURE_VIEW, {
      categoryCode: CATEGORY_CODE.JOB_DETAILS,
      includeAggregates: false,
      isGroupingEnabled: false,
      navigateToPrevSlideout: showJobDetailsSlideout,
      onRefresh: refreshViews,
      page: PAGE.JOB_DETAILS,
      pageCode: PAGE_CODE.JOB_DETAILS,
      pageName: 'Job Detail Views'
    });
  }

  function showJobDetailsSlideout() {
    showSlideout(MP_SLIDEOUTS.JOB_DETAILS, {
      groupAdminCanConfigureViews,
      jobCode,
      jobCodeKey,
      regularUserCanConfigureViews,
      userRights
    });
  }

  return (
    <div className="job-details__container">
      <SlideoutHeader title="Job Details" doClose={onClose} />
      <div className="job-details__sub-header">{getViewDropdown()}</div>
      <ActionableHeaderCard cardTitle="Job Data" className="job-details__card" isCollapsible={false}>
        <div className="job-details__table">{renderJobDetailData()}</div>
      </ActionableHeaderCard>
    </div>
  );
}

const mapStateToProps = ({ jobDetailsData, session }) => {
  return {
    groupAdminCanConfigureViews: session.groupAdminCanConfigureViews,
    jobDetails: jobDetailsData.jobDetailsData,
    loading: jobDetailsData.loading,
    regularUserCanConfigureViews: session.regularUserCanConfigureViews,
    selectedViewPk: jobDetailsData.jobDetailsSelectedView?.selectedViewPk,
    userRights: session.user.rights,
    views: jobDetailsData.jobDetailsViews?.views,
    showJobCodeKey: session.organization.useJobCodeKey
  };
};

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(
    {
      getJobDetails,
      getJobDetailsSelectedView,
      getJobDetailsViews,
      setJobDetailsSelectedView
    },
    dispatch
  )
});

export default connect(mapStateToProps, mapDispatchToProps)(JobDetails);
